import type { ReactNode } from "react";
import { ChevronRight, Map } from "lucide-react";
import { DataTable, type DataTableColumn, type DataTableServerPagination } from "@/components/ui/data-table";
import { UserAvatar } from "@/components/ui/user-avatar";
import { InfoTooltip } from "@/lib/dashboard/InfoTooltip";
import { PaymentStatusCell } from "@/lib/dashboard/shared/PaymentStatusCell";
import { formatPlanDate } from "@/lib/builder/utils/payment-format";
import { useI18n } from "@/hooks/useI18n";
import { AgencyTripRow } from "@/integrations/supabase/rpc-types";
import {
  TripStateBadge,
  tripAdvisorName,
  tripClientName,
} from "@/lib/dashboard/trips/trip-display";

interface TripsTableProps {
  rows: AgencyTripRow[];
  /** Riga aperta nel pannello: resta evidenziata sotto lo sheet. */
  selectedId: string | null;
  onSelect: (row: AgencyTripRow) => void;
  showAdvisorColumn: boolean;
  pageSize: number;
  serverPagination: DataTableServerPagination;
  isLoading: boolean;
  /** `version` del caricamento: le righe rientrano animate a ogni pagina arrivata. */
  animationKey: number;
  emptyState: ReactNode;
}

export function TripsTable({
  rows,
  selectedId,
  onSelect,
  showAdvisorColumn,
  pageSize,
  serverPagination,
  isLoading,
  animationKey,
  emptyState,
}: TripsTableProps) {
  const { t } = useI18n();

  const columns: DataTableColumn<AgencyTripRow>[] = [
    {
      id: "trip",
      header: t("dashboard.overview.trips.columnTrip"),
      cellClassName: "font-medium text-gray-900",
      cell: (row) => (
        <div className="flex flex-col gap-0.5 max-w-[320px]">
          <span className="truncate">{row.title}</span>
          <span className="truncate text-xs text-gray-500 font-normal">
            {row.destinations.join(", ") || "—"}
          </span>
        </div>
      ),
    },
    {
      id: "client",
      header: t("dashboard.overview.trips.columnClient"),
      cell: (row) => (
        <UserAvatar
          expanded
          firstName={row.client_first_name}
          lastName={row.client_last_name}
          fullName={tripClientName(row)}
          email={row.client_email}
          imageUrl={row.client_avatar_url}
          size="sm"
        />
      ),
    },
    ...(showAdvisorColumn
      ? [
          {
            id: "advisor",
            header: t("dashboard.overview.trips.columnAdvisor"),
            headClassName: "w-px whitespace-nowrap",
            cellClassName: "text-gray-600 whitespace-nowrap",
            cell: (row: AgencyTripRow) => tripAdvisorName(row) ?? "—",
          },
        ]
      : []),
    {
      id: "state",
      header: t("dashboard.overview.trips.columnState"),
      headClassName: "w-px whitespace-nowrap",
      cell: (row) => <TripStateBadge state={row.trip_state} />,
    },
    {
      id: "dates",
      header: t("dashboard.overview.trips.columnDates"),
      headClassName: "w-px whitespace-nowrap",
      cellClassName: "text-gray-600 whitespace-nowrap text-sm",
      cell: (row) =>
        row.start_date
          ? `${formatPlanDate(row.start_date)}${row.end_date ? ` – ${formatPlanDate(row.end_date)}` : ""}`
          : "—",
    },
    {
      id: "payment",
      header: (
        <span className="inline-flex items-center gap-1.5">
          {t("dashboard.overview.paymentStatus.column")}
          <InfoTooltip text={t("dashboard.overview.trips.paymentInfo")} />
        </span>
      ),
      headClassName: "w-px whitespace-nowrap text-right",
      cellClassName: "text-right",
      cell: (row) => (
        <PaymentStatusCell
          compact
          paidAmount={row.paid_amount}
          pendingAmount={row.remaining_amount}
          overdueAmount={row.overdue_amount}
          totalAmount={row.plan_total}
          empty={!row.has_plan}
        />
      ),
    },
    {
      id: "open",
      header: null,
      headClassName: "w-8",
      cellClassName: "text-right",
      cell: () => <ChevronRight className="h-4 w-4 text-gray-400" />,
    },
  ];

  return (
    <DataTable
      data={rows}
      columns={columns}
      getRowId={(row) => row.itinerary_id}
      onRowClick={onSelect}
      selectedRowId={selectedId}
      pageSize={pageSize}
      serverPagination={serverPagination}
      isLoading={isLoading}
      animationKey={animationKey}
      emptyState={emptyState}
      tableClassName="table-auto min-w-[900px]"
    />
  );
}

/** Vuoto per davvero e vuoto per colpa dei filtri sono due messaggi diversi. */
export function TripsEmpty({ filtered }: { filtered: boolean }) {
  const { t } = useI18n();

  return (
    <div className="flex flex-col items-center gap-2 text-center text-gray-500">
      <Map className="h-8 w-8 text-gray-300" />
      <p className="text-sm">
        {filtered
          ? t("dashboard.overview.trips.emptyFiltered")
          : t("dashboard.overview.trips.empty")}
      </p>
    </div>
  );
}

export default TripsTable;
